// src/component/PreviewMMInfoTabs.js
// 擬似トップページ用：MMInfoTabs.js のコピー
// 本家とは独立して編集可能
import React, { useContext, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material";

import SmartDisplayRoundedIcon from "@mui/icons-material/SmartDisplayRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import WbSunnyRoundedIcon from "@mui/icons-material/WbSunnyRounded";

import { AppThemeContext } from "./AppShell";
import MMVideoList from "./PreviewMMVideoList";
import DailyNewsDialog from "./PreviewDailyNewsDialog";
import MMInfoFeedList from "./PreviewMMInfoFeedList";
import ImportantAnnouncementList from "./PreviewImportantAnnouncementList";

function a11yProps(index) {
  return {
    id: `mm-info-tab-${index}`,
    "aria-controls": `mm-info-tabpanel-${index}`,
  };
}

function TabPanel({ children, value, index }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`mm-info-tabpanel-${index}`}
      aria-labelledby={`mm-info-tab-${index}`}
      sx={{ pt: 2 }}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  );
}

/**
 * MasterMind トップ用のタブ
 * - 動画
 * - 最新情報（固定系）
 * - 基本情報（流れていく系）
 */
export default function MMInfoTabs() {
  const themeFromAppShell = useContext(AppThemeContext) || {};
  const accent = themeFromAppShell.railActive || "#5A35FF";
  const surface = themeFromAppShell.surfaceBg || "#FFFFFF";
  const border = themeFromAppShell.bottomBorder || "rgba(0,0,0,0.06)";
  const textBase = themeFromAppShell.railText || "rgba(40,40,60,0.9)";

  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [value, setValue] = useState(0);

  const handleChange = (_e, newValue) => {
    setValue(newValue);
  };

  return (
    <Box
      sx={{
        mt: 3,
        borderRadius: 3,
        bgcolor: `${surface}F6`,
        border: `1px solid ${border}`,
        boxShadow: "0 16px 40px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      {/* タブヘッダー */}
      <Box
        sx={{
          px: { xs: 1, sm: 2 },
          pt: 1.5,
          pb: 0.5,
          borderBottom: `1px solid ${border}`,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,255,255,0.90))",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          aria-label="MasterMind 情報タブ"
          sx={{
            minHeight: 0,
            "& .MuiTabs-indicator": { display: "none" },
          }}
        >
          <FancyTab
            value={0}
            icon={<BookmarkRoundedIcon />}
            label="最新情報"
            accent={accent}
            textBase={textBase}
            {...a11yProps(0)}
          />
          <FancyTab
            value={1}
            icon={<WbSunnyRoundedIcon />}
            label="基本情報"
            accent={accent}
            textBase={textBase}
            {...a11yProps(1)}
          />
          <FancyTab
            value={2}
            icon={<SmartDisplayRoundedIcon />}
            label="動画"
            accent={accent}
            textBase={textBase}
            {...a11yProps(2)}
          />
        </Tabs>
      </Box>

      {/* タブの中身（中身はあとで差し替えてOK） */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pb: 2.5,
          pt: 1,

          // ★ 追加：固定高さ（モバイルは画面下まで）
          height: {
            xs: "calc(100dvh - 240px)", // モバイル：AppBar + Tabs + 下ナビぶん引く
            sm: "calc(100dvh - 380px)", // タブレット以上
          },

          overflowY: "auto",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch", // スムーズスクロール
        }}
      >

        <TabPanel value={value} index={0}>
          <MMInfoFeedList />
        </TabPanel>

        <TabPanel value={value} index={1}>
          <ImportantAnnouncementList />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <MMVideoList />
        </TabPanel>
      </Box>
    </Box>
  );
}

/** 仮カード（あとで Firestore 表示に差し替え） */
function PlaceholderCard({ title }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        px: 1.5,
        py: 1,
        bgcolor: "rgba(255,255,255,0.9)",
        border: "1px dashed rgba(0,0,0,0.08)",
      }}
    >
      <Typography variant="body2" sx={{ opacity: 0.7 }}>
        {title}
      </Typography>
    </Box>
  );
}

/** グラデーション付きのタブ */
function FancyTab({ icon, label, accent, textBase, ...tabProps }) {
  return (
    <Tab
      disableRipple
      icon={icon}
      iconPosition="start"
      label={label}
      {...tabProps} // ← ここに value / id / aria-*** が全部入る
      sx={{
        position: "relative",
        minHeight: 40,
        px: { xs: 1.4, sm: 2 },
        py: 0.8,
        mr: 1,
        borderRadius: 999,
        textTransform: "none",
        fontWeight: 600,
        fontSize: 13,
        color: textBase,
        zIndex: 1,

        "& .MuiSvgIcon-root": {
          fontSize: 18,
          mr: 0.5,
        },

        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: 999,
          background: `linear-gradient(90deg, ${accent}30, ${accent}15)`,
          boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
          opacity: 0,
          transform: "scale(0.92)",
          transition: "opacity .25s ease, transform .25s ease",
          zIndex: -1,
        },

        "&.Mui-selected": {
          color: accent,
          "&::before": {
            opacity: 1,
            transform: "scale(1)",
          },
        },
      }}
    />
  );
}
